/**
 * This component is used in the category faceted search pages
 * Displays image, name, short desc, and quick adds
 */

import type { Cart } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import type {
  BaseProduct,
  FlavourCampaignProduct,
  Variant,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { isCloudinaryAsset } from '@bulknutrients/bulk-shared-lib/lib/images';
import { isVariantAvailable } from '@bulknutrients/bulk-shared-lib/lib/product';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { AddToFavourites, Button, QuickAddButton } from '@components/Button';
import {
  AddToCartCustomChildren,
  AddToCartStatus,
} from '@components/Button/AddToCart';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Select } from '@components/FormComponents/FormComponents';
import { CustomImage } from '@components/Media/Image/Image';
import { SkeletonImage, SkeletonText } from '@components/Placeholders/Skeleton';
import { getSelectOptions } from '@components/Product/Options';
import { StarRating } from '@components/Product/StarRating';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useAddItems } from '@hooks/cart/useAddItems';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useStockLevel } from '@hooks/useStockLevel';
import { logBreadcrumb } from '@lib/logs';
import Link from 'next/link';
import {
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

export function Price({
  price,
  basePrice,
  onSale,
}: {
  onSale: boolean;
  price: number;
  basePrice: number | undefined;
}): ReactElement {
  return (
    <span className="price text-2xl font-secondary font-bold content-center">
      {onSale && basePrice && (
        <span className="relative font-normal before:absolute before:left-[-5%] before:top-[45%] before:mx-auto before:my-0 before:w-[110%] before:skew-y-[-20deg] before:border-0 before:border-t-3 before:border-solid before:border-t-orange before:opacity-60 before:content-[''] mr-2">
          ${basePrice.toFixed(2)}
        </span>
      )}
      <span>${price.toFixed(2)}</span>
    </span>
  );
}

export function PriceRange({
  minPrice,
  maxPrice,
}: {
  minPrice: number;
  maxPrice: number;
}): ReactElement {
  const [minDollar, minCents] = minPrice.toFixed(2).split('.');
  const [maxDollar, maxCents] = maxPrice.toFixed(2).split('.');

  return (
    <div className="font-bold dark:text-white text-lg">
      <span className="mx-1 my-0 inline-flex items-start">
        $<span className="text-2xl">{minDollar}</span>.{minCents}{' '}
      </span>
      -
      <span className="mx-1 my-0 inline-flex items-start">
        $<span className="text-2xl">{maxDollar}</span>.{maxCents}
      </span>
    </div>
  );
}

export function ProductCard({
  product,
  className,
  location,
}: {
  product: BaseProduct;
  className?: string;
  location?: string; // Optional location for umami tracking
}): ReactElement {
  return (
    <ProductCardHorizontalVariant
      product={product}
      className={className}
      location={location}
    />
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="grid gap-2 grid-cols-[150px_auto] max-w-screen sm:w-[450px] content-between gap-0 bg-grey-light dark:bg-grey-20 text-black dark:text-white rounded-2xl overflow-hidden cursor-default">
      <SkeletonImage widthPixels={150} />
      <SkeletonText heightPixels={140} />
    </div>
  );
}

export function ProductCardVerticalVariant({
  product,
  className,
  location,
}: {
  product: BaseProduct;
  className?: string;
  location?: string;
}): ReactElement {
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [shouldCheckStock, setShouldCheckStock] = useState(false);
  const [resolvedVariant, setResolvedVariant] = useState<Variant | null>(null);
  const cardElement = useRef<HTMLDivElement | null>(null);

  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });

  const { setQuickCartIsOpen, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  const { stockLevels } = useStockLevel(product.entityId.toString(), {
    enabled: shouldCheckStock,
  });
  const { customer } = useCustomer();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
  });

  const optionChanged = rhfMethods.watch();

  const defaultVariant = product.variants.find(
    (v) => v.variantId === product.defaultVariantId
  );

  const {
    basePrice,
    salePrice,
    price: initPrice,
    priceRange,
  } = product.pricing;

  const isOnSale = salePrice?.value === initPrice.value;

  const image =
    product.images.filter((i) => i.defaultImage)[0] ?? product.images[0];

  const availableVariants = product.variants.filter((v) =>
    isVariantAvailable(v, customer ? customer.customerGroupId : undefined)
  );

  // Get the options requiring selection (e.g. Flavour)
  const selectableOptionIds = useMemo(
    () =>
      product.options
        .filter(
          (option) =>
            getSelectOptions(option, availableVariants, rhfMethods, stockLevels)
              .length > 1
        )
        .map((option) => option.entityId),
    [availableVariants, product.options, rhfMethods, stockLevels]
  );

  // Use an intersection observer to only check stock levels when the card is in view,
  // to avoid unnecessary API calls and improve performance
  useEffect(() => {
    if (shouldCheckStock || !cardElement.current) {
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldCheckStock(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldCheckStock(true);
            observer.disconnect();
            break;
          }
        }
      },
      {
        rootMargin: '200px 0px',
      }
    );

    observer.observe(cardElement.current);

    return () => {
      observer.disconnect();
    };
  }, [shouldCheckStock]);

  useEffect(() => {
    const variantSelected = getVariantFromFormSelection();
    setResolvedVariant(variantSelected);
  }, [optionChanged]);

  const singleInStockVariant = useMemo(() => {
    if (!stockLevels) {
      return null;
    }

    const inStockVariants = availableVariants.filter(
      (variant) => stockLevels[variant.variantId]
    );

    return inStockVariants.length === 1 ? inStockVariants[0] : null;
  }, [availableVariants, stockLevels]);

  if (
    !product.images ||
    !product.images.filter((i) => i.defaultImage)[0] ||
    !product.slug
  ) {
    return <></>;
  }

  const requiresOptionSelection = availableVariants.length > 1;
  const optionsAreLoading =
    requiresOptionSelection && shouldCheckStock && !stockLevels;

  const getVariantFromFormSelection = (): Variant | null => {
    // If isVariantAvailable has filtered out all variants (e.g. a product with a single
    // no-option variant), fall back to the default variant so it can still be added to cart.
    if (availableVariants.length === 0 && defaultVariant) {
      return defaultVariant;
    }

    const selectedOptions = rhfMethods.getValues();

    // Have I selected something for all the options that require selection?
    // if not return null and prompt user to select
    const matchedVariants = availableVariants.filter(
      (pv) =>
        pv.options?.length > 0 &&
        selectableOptionIds.every((optionId) => {
          const variantOption = pv.options?.find(
            (o) => o.optionId === optionId
          );
          if (!variantOption) return false;

          return Number(selectedOptions[optionId]) === variantOption.valueId;
        })
    );

    if (matchedVariants.length === 1) {
      return matchedVariants[0];
    }

    // If stock data shows exactly one purchasable variant, auto-resolve it.
    // This avoids requiring form selections when only one in-stock SKU exists.
    if (singleInStockVariant) {
      return singleInStockVariant;
    }

    // No selection needed - single available variant
    if (selectableOptionIds.length === 0 && availableVariants.length === 1) {
      return availableVariants[0];
    }

    // Edge case: no selectable options but multiple variants - fall back to default
    if (
      selectableOptionIds.length === 0 &&
      defaultVariant &&
      availableVariants.some((v) => v.variantId === defaultVariant.variantId)
    ) {
      return defaultVariant;
    }

    return null;
  };

  const handleQuickBuy = async () => {
    setShouldCheckStock(true);

    // On first click, stock may not be loaded yet for optioned products.
    if (requiresOptionSelection && !stockLevels) {
      setError(true);
      setErrorMessage(
        <Paragraph>
          Loading available options. Please try again in a moment.
        </Paragraph>
      );
      return;
    }

    if (optionsAreLoading) {
      setError(true);
      setErrorMessage(
        <Paragraph>
          Loading available options. Please try again in a moment.
        </Paragraph>
      );
      return;
    }

    if (!resolvedVariant) {
      handleQuickBuyInvalid();
      return;
    }

    logBreadcrumb({
      category: 'AddToCart',
      message: 'Clicked add to cart button',
      level: 'info',
      data: {
        productId: resolvedVariant.productId,
        variantId: resolvedVariant.variantId,
        quantity: 1,
      },
    });

    let cartResponse: { data: Cart | null; error: string | null };
    try {
      setAddingToCartStatus(AddToCartStatus.Adding);
      cartResponse = await addItem({
        lineItems: [
          {
            productId: resolvedVariant.productId,
            variantId: resolvedVariant.variantId,
            quantity: 1,
          },
        ],
      });

      if (cartResponse.error !== null) {
        setError(true);
        if (cartResponse.error) {
          setErrorMessage(
            <Paragraph>
              We couldn't add that to your cart. {cartResponse.error}
            </Paragraph>
          );
        } else {
          throw new Error('Unexpected error');
        }
      } else {
        setQuickCartIsOpen(true);
      }
      setAddingToCartStatus(AddToCartStatus.Added);
    } catch {
      setAddingToCartStatus(AddToCartStatus.Failed);
      setError(true);
      setErrorMessage(
        <Paragraph>
          We couldn't add that to your cart. If you continue to have issues,
          please contact customer service.
        </Paragraph>
      );
    }
  };

  const handleQuickBuyInvalid = () => {
    setShouldCheckStock(true);
    setError(true);
    setErrorMessage(
      <Paragraph>Please make a selection before adding to cart</Paragraph>
    );
  };

  return (
    <div
      ref={cardElement}
      className={mergeStyles(
        className ?? '',
        'flex rounded-2xl max-w-[275px] h-full overflow-hidden flex-col gap-0 bg-grey-light dark:bg-grey-20 transition-all duration-300 hover:scale-103 has-[[data-select-open]]:scale-103'
      )}
    >
      <div className="relative">
        <Link
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
          href={product.slug}
          data-umami-event="hue-product-card-interaction"
          data-umami-event-interaction="image-link-clicked"
          data-umami-event-productid={product.entityId}
          data-umami-event-location={`productCard${location ? ` inside ${location}` : ''}`}
        >
          <CustomImage
            image={image}
            width={275}
            height={300}
            className="rounded-none"
            templateOverride={{
              crop: 'auto',
              ratio: '275:300',
              gravity: 'auto',
            }}
          />
        </Link>
        <div className="absolute top-1 right-1">
          <AddToFavourites
            productId={product.entityId}
            sku={product.sku}
            className="[&_.filled-favourites-icon]:dark:text-white [&_.outline-favourites-icon]:dark:text-white"
          />
        </div>
      </div>
      <div className="relative flex flex-1 flex-col py-3 text-black dark:text-white">
        <div className="flex flex-col items-center px-2 sm:px-4">
          <h3 className="font-secondary uppercase">
            <Link
              prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
              href={product.slug}
              className="clickableNoLine hover:underline line-clamp-1 font-secondary text-2xl text-black dark:text-white"
              data-umami-event="hue-product-card-title-link"
              data-umami-event-productid={product.entityId}
            >
              {product.shortName || product.name}
            </Link>
          </h3>
          <Price
            onSale={isOnSale && basePrice !== null}
            basePrice={basePrice?.value}
            price={
              resolvedVariant
                ? resolvedVariant.pricing.price.value
                : priceRange.min.value
            }
          />
        </div>

        <div className="grow my-1 px-2 sm:px-4">
          <div className="line-clamp-3 text-ellipsis text-sm [display:-webkit-box] pointer-events-none">
            {product.categoryBulletPoints ? (
              <div className="flex flex-col gap-2">
                {product.categoryBulletPoints.map((bullet) => (
                  <span key={bullet}>{bullet}</span>
                ))}
              </div>
            ) : (
              product.shortDescription
            )}
          </div>
        </div>

        <div className="flex gap-1 justify-center my-1">
          <StarRating rating={product.averageRating} /> ({product.totalReviews})
        </div>

        <div className="flex flex-col items-center mt-4 px-2 sm:px-4">
          <FormProvider {...rhfMethods}>
            <form
              className="w-full"
              onSubmit={rhfMethods.handleSubmit(
                handleQuickBuy,
                handleQuickBuyInvalid
              )}
            >
              {product.options.map((option) => {
                const selectOptions = getSelectOptions(
                  option,
                  availableVariants,
                  rhfMethods,
                  stockLevels
                );

                if (selectOptions.length === 1) return null;

                return (
                  <Select
                    key={option.entityId}
                    name={option.entityId.toString()}
                    label="Select an option"
                    className="w-full"
                    options={selectOptions}
                  />
                );
              })}
              <input type="hidden" value={product.entityId} name="productId" />
              <input
                type="hidden"
                value={resolvedVariant?.variantId}
                name="variantId"
              />
              <input type="hidden" value={1} name="quantity" />
              <input
                type="hidden"
                value={resolvedVariant?.sku || product.sku}
                name="sku"
              />
              <input type="hidden" value={product.name} name="productName" />
              <TooltipWrapper
                text={
                  resolvedVariant === null &&
                  product.options &&
                  product.options.length > 0
                    ? 'Select an option above'
                    : 'Add to cart'
                }
              >
                <Button
                  buttonStyle="secondary"
                  type="submit"
                  className="w-full py-1"
                  disabled={
                    rhfMethods.formState.isSubmitting ||
                    optionsAreLoading ||
                    !resolvedVariant
                  }
                  data-umami-event="hue-quick-buy-clicked"
                  data-umami-event-productid={resolvedVariant?.productId}
                  data-umami-event-variantid={resolvedVariant?.variantId}
                >
                  {optionsAreLoading ? 'Loading options...' : 'Quick Buy'}
                </Button>
              </TooltipWrapper>
            </form>
          </FormProvider>
        </div>
      </div>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
    </div>
  );
}

export function ProductCardFlavourCampaign({
  product,
  className,
  location,
}: {
  product: FlavourCampaignProduct;
  className?: string;
  location?: string;
}): ReactElement {
  const [shouldCheckStock, setShouldCheckStock] = useState(false);
  const cardElement = useRef<HTMLDivElement | null>(null);

  const {
    basePrice,
    salePrice,
    price: initPrice,
    priceRange,
  } = product.pricing;

  const isOnSale = salePrice?.value === initPrice.value;

  // Use an intersection observer to only check stock levels when the card is in view,
  // to avoid unnecessary API calls and improve performance
  useEffect(() => {
    if (shouldCheckStock || !cardElement.current) {
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldCheckStock(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldCheckStock(true);
            observer.disconnect();
            break;
          }
        }
      },
      {
        rootMargin: '200px 0px',
      }
    );

    observer.observe(cardElement.current);

    return () => {
      observer.disconnect();
    };
  }, [shouldCheckStock]);

  return (
    <div className="relative mb-5">
      <div
        ref={cardElement}
        className={mergeStyles(
          className ?? '',
          'flex rounded-2xl max-w-[275px] h-full overflow-hidden flex-col gap-0 bg-grey-light dark:bg-grey-20 transition-all duration-300 hover:scale-103 has-[[data-select-open]]:scale-103'
        )}
      >
        <div className="relative">
          <Link
            prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
            href={product.slug}
            data-umami-event="hue-product-card-interaction"
            data-umami-event-interaction="image-link-clicked"
            data-umami-event-productid={product.entityId}
            data-umami-event-location={`productCard${location ? ` inside ${location}` : ''}`}
          >
            <CustomImage
              image={product.productCardImage}
              width={275}
              height={300}
              className="rounded-none"
              templateOverride={
                isCloudinaryAsset(product.productCardImage) &&
                !product.productCardImage.asset.derived?.[0].raw_transformation.includes(
                  'c_auto'
                )
                  ? { crop: 'auto', ratio: '1:1' }
                  : undefined
              }
            />
          </Link>
          <div className="absolute top-1 right-1">
            <AddToFavourites
              productId={product.entityId}
              sku={product.sku}
              className="[&_.filled-favourites-icon]:dark:text-white [&_.outline-favourites-icon]:dark:text-white"
            />
          </div>
        </div>

        <div className="relative py-3 flex flex-1 flex-col text-black dark:text-white">
          <div className="flex flex-col items-center px-2 sm:px-4">
            <h3 className="font-secondary uppercase">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                href={product.slug}
                className="clickableNoLine hover:underline line-clamp-1 font-secondary text-2xl text-black dark:text-white block text-center"
                data-umami-event="hue-product-card-title-link"
                data-umami-event-productid={product.entityId}
              >
                {product.shortName || product.name} <br />
                {product.variants
                  .filter(
                    (v) => v.variantId === product.selectedVariant?.variantId
                  )[0]
                  ?.options.filter(
                    (o) => o.displayName.toLowerCase() === 'flavour'
                  )
                  .map((o) => o.label)
                  .join(', ')}
              </Link>
            </h3>
            <Price
              onSale={isOnSale && basePrice !== null}
              basePrice={basePrice?.value}
              price={
                product.variants.filter(
                  (v) => v.variantId === product.selectedVariant?.variantId
                )[0]
                  ? product.variants.filter(
                      (v) => v.variantId === product.selectedVariant?.variantId
                    )[0].pricing.price.value
                  : priceRange.min.value
              }
            />
          </div>

          <div className="grow my-1 px-2 sm:px-4">
            <div className="text-sm [display:-webkit-box] whitespace-pre-line">
              {typeof product.shortDescription === 'string'
                ? product.shortDescription
                : product.shortDescription.join('\n')}
            </div>
          </div>

          <div className="flex gap-1 justify-center my-1">
            <StarRating rating={product.averageRating} /> (
            {product.totalReviews})
          </div>

          <div className="mt-4 px-2 sm:px-4">
            <AddToCartCustomChildren
              productId={product.entityId}
              variantId={product.selectedVariant?.variantId}
              quantity={1}
              sku={product.selectedVariant?.sku || product.sku}
              name={product.name}
              className="w-full"
              buttonStyle="secondary"
            >
              Quick Buy
            </AddToCartCustomChildren>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductCardHorizontalVariant({
  product,
  className,
  location,
  quickCart = false,
}: {
  product: BaseProduct;
  className?: string;
  location?: string; // Optional location for umami tracking
  quickCart?: boolean;
}): ReactElement {
  const {
    basePrice,
    salePrice,
    price: initPrice,
    priceRange,
  } = product.pricing;

  const isOnSale = salePrice?.value === initPrice.value;

  return (
    <div
      className={mergeStyles(
        className ?? '',
        twJoin([
          'grid grid-cols-[100px_auto] max-w-screen sm:max-w-[450px] content-between gap-0',
          'bg-grey-light dark:bg-grey-20 text-black dark:text-white rounded-2xl overflow-hidden cursor-default',
          'transition-all duration-300 hover:scale-103',
          // this breakpoint differentiation matters for horizontal cards in quick cart vs normal product card lists
          quickCart
            ? 'sm:grid-cols-[150px_auto]'
            : 'tiny:grid-cols-[150px_auto]',
        ])
      )}
    >
      <div className="relative">
        <Link
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
          href={product.slug}
          data-umami-event="hue-product-card-interaction"
          data-umami-event-interaction="image-link-clicked"
          data-umami-event-productid={product.entityId}
          data-umami-event-location={`productCard${location ? ` inside ${location}` : ''}`}
        >
          <CustomImage
            image={
              product.images.filter((i) => i.defaultImage)[0] ??
              product.images[0]
            }
            width={150}
            height={150}
            className={twJoin([
              'w-[100px] rounded-none',
              // this breakpoint differentiation matters for horizontal cards in quick cart vs normal product card lists
              quickCart ? 'sm:w-full' : 'tiny:w-full',
            ])}
          />
        </Link>

        <div className="absolute top-1 right-1">
          <AddToFavourites
            productId={product.entityId}
            sku={product.sku}
            className="[&_#favourites-wrapper]:bg-white [&_#filled-favourites-icon]:dark:text-black [&_#outline-favourites-icon]:dark:text-black [&_#favourites-wrapper]:rounded-full [&_#favourites-wrapper]:p-1"
          />
        </div>
      </div>

      <div className="relative flex flex-1 flex-col pt-2 pb-1 tiny:pb-7 px-4">
        <div className="flex flex-col tiny:flex-row justify-between gap-4">
          <h3 className="font-secondary uppercase">
            <Link
              prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
              href={product.slug}
              className="line-clamp-1 cursor-pointer font-secondary text-2xl font-semibold decoration-grey-mid duration-500 underline-offset-[0.1em] dark:text-white
              hover:underline hover:decoration-orange hover:decoration-3 hover:duration-200 
              active:underline active:decoration-orange active:decoration-3 active:duration-200"
              data-umami-event="hue-product-card-title-link"
              data-umami-event-productid={product.entityId}
            >
              {product.shortName || product.name}
            </Link>
          </h3>
          <Price
            onSale={isOnSale && basePrice !== null}
            basePrice={basePrice?.value}
            price={priceRange.min.value}
          />
        </div>

        <div className={`flex-1 ${quickCart ? 'hidden sm:flex' : 'flex'}`}>
          <div className="line-clamp-3 text-ellipsis text-sm leading-4">
            {product.shortDescription}
          </div>
        </div>

        {product.discontinued ? null : (
          <div className="absolute bottom-4 right-3 tiny:static justify-end flex items-center">
            <QuickAddButton
              product={product}
              className="hover:scale-110 active:scale-110 w-fit dark:text-white h-9"
              data-umami-event-location={`productCard${location ? ` inside ${location}` : ''}`}
            />
          </div>
        )}

        <div className="hidden tiny:flex bg-black dark:bg-grey-15 absolute bottom-0 left-0 w-full px-4 py-1 flex-row justify-between">
          <div className="flex items-center font-secondary font-medium tracking-wider text-[1rem] uppercase text-white">
            {product.pricePerServe && product.pricePerServe > 0 ? (
              <>
                {product.pricePerServe < 1 ? (
                  <span className="lowercase">
                    {Math.round(product.pricePerServe * 100).toFixed(0)}c{' '}
                  </span>
                ) : (
                  `$${product.pricePerServe.toFixed(2)}`
                )}
                &nbsp; per serve
              </>
            ) : null}
          </div>
          <StarRating rating={product.averageRating} />
        </div>
      </div>
      <div className="tiny:hidden col-span-full bg-black dark:bg-grey-15 w-full px-4 py-1 flex flex-row justify-between">
        <div className="flex items-center font-secondary font-medium tracking-wider text-[1rem] uppercase text-white">
          {product.pricePerServe && product.pricePerServe > 0 ? (
            <>
              {product.pricePerServe < 1 ? (
                <span className="lowercase">
                  {Math.round(product.pricePerServe * 100).toFixed(0)}c{' '}
                </span>
              ) : (
                `$${product.pricePerServe.toFixed(2)}`
              )}
              &nbsp; per serve
            </>
          ) : null}
        </div>
        <StarRating rating={product.averageRating} />
      </div>
    </div>
  );
}

/**
 * Has breakpoints slightly different to the ProductCard component.
 * Hides info like rating text, price per serve etc on smallest screens. And description.
 * More targeted because of reduced width on the slide in menu on mobile
 * @param
 * @returns
 */
export function ProductCardQuickCartCarousel({
  product,
  className,
  location,
}: {
  product: BaseProduct;
  className?: string;
  location?: string; // Optional location for umami tracking
}): ReactElement {
  return (
    <ProductCardHorizontalVariant
      product={product}
      className={className}
      location={location}
      quickCart={true}
    />
  );
}

export function ProductCardAutocompleteSearch({
  product,
  className,
  location,
}: {
  product: BaseProduct;
  className?: string;
  location?: string; // Optional location for umami tracking
}): ReactElement {
  const {
    basePrice,
    salePrice,
    price: initPrice,
    priceRange,
  } = product.pricing;

  const isOnSale = salePrice?.value === initPrice.value;

  // show min value if no range or the product is on sale
  const useMinPrice = priceRange.min.value === priceRange.max.value || isOnSale;

  if (!product.images?.filter((i) => i.defaultImage)[0] || !product.slug) {
    // TODO: this is a temp fix for gift certs being broken
    return <></>;
  }

  return (
    <div
      className={mergeStyles(
        className ?? '',
        'grid max-w-[450px] grid-cols-[100px_auto] content-between gap-4 sm:grid-cols-[150px_auto] h-[100px] sm:h-[150px]'
      )}
    >
      <Link
        prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
        href={product.slug}
        data-umami-event="hue-product-card-interaction"
        data-umami-event-interaction="image-link-clicked"
        data-umami-event-productid={product.entityId}
        data-umami-event-location={`productCard${location ? ` inside ${location}` : ''}`}
      >
        <CustomImage
          image={
            product.images.filter((i) => i.defaultImage)[0] ?? product.images[0]
          }
          width={150}
          height={150}
          templateOverride={{ crop: 'auto', ratio: '1:1' }}
          className="w-[100px] sm:w-full"
        />
      </Link>

      <div className="relative flex justify-between min-h-[100px] flex-col px-2 text-grey-dark dark:text-white tiny:min-h-[150px]">
        <div>
          <h3 className="mt-1 font-secondary uppercase">
            <Link
              prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
              href={product.slug}
              className="line-clamp-1 cursor-pointer font-secondary text-2xl font-semibold text-grey-dark underline decoration-grey-mid decoration-1 underline-offset-[0.1em] duration-500 hover:text-black hover:decoration-orange hover:decoration-3 hover:duration-200 dark:text-white dark:hover:text-white"
              data-umami-event="hue-product-card-title-link"
              data-umami-event-productid={product.entityId}
            >
              {product.shortName || product.name}
            </Link>
          </h3>

          <div className="font-primary text-2xl font-bold mb-1">
            {useMinPrice ? (
              <Price
                onSale={isOnSale && basePrice !== null}
                basePrice={basePrice?.value}
                price={priceRange.min.value}
              />
            ) : (
              <PriceRange
                minPrice={priceRange.min.value}
                maxPrice={priceRange.max.value}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
